import { useEffect, useState, useCallback, useRef } from "react";
import { getPendingCount, syncPendingRequests } from "../utils/offlineSync";

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const wasOfflineRef = useRef(!navigator.onLine);
  const syncingRef = useRef(false);

  const syncNow = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return null;
    syncingRef.current = true;
    setSyncing(true);
    try {
      const result = await syncPendingRequests();
      setLastSyncResult(result);
      const count = await getPendingCount();
      setPendingCount(count);
      return result;
    } catch {
      return null;
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, []);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
        syncNow();
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [syncNow]);

  // Poll pending count less frequently, use ref to avoid re-renders when count unchanged
  useEffect(() => {
    let mounted = true;
    const check = async () => {
      const count = await getPendingCount();
      if (mounted) {
        setPendingCount(prev => prev === count ? prev : count);
      }
    };
    check();
    const interval = setInterval(check, 30000); // 30s instead of 5s
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  // Listen for sync messages from service worker
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "SYNC_SUCCESS") {
        setPendingCount((c) => Math.max(0, c - 1));
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []);

  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  return {
    isOnline,
    pendingCount,
    syncing,
    lastSyncResult,
    syncNow,
    refreshPendingCount,
  };
}
